import { FC, useEffect } from 'react';

import { useTranslation } from '@crehana/i18n';
import { getNextUrl } from '@crehana/utils';
import type { MeObject } from '@crehana/web';

import { AuthFormSocialloginMutation } from '@/shared/graphql/types/AuthFormSocialloginMutation';

import { useSocialAuth } from '../../hooks/useSocialAuth';
import SocialButtonsMinimal from './SocialButtonsMinimal';

export interface SocialButtonsProps {
  shouldRenderFacebookButton?: boolean;
  referFriendId?: string;
  nextUrl?: string;
  customHandler?(params: {
    redirect?: string | null;
    user?: MeObject;
    isNewUser?: boolean | null;
    graphqlResponse?: AuthFormSocialloginMutation['socialLogin'];
  }): void;
  currentForm?: string;
  hideFacebookButton?: boolean;
  hideFacebookLink?: boolean;
  showMicrosoftButton?: boolean;
  friendInvitationId?: string;
  authType?: 'signUp' | 'signIn';
}

const SocialButtons: FC<React.PropsWithChildren<SocialButtonsProps>> = ({
  shouldRenderFacebookButton = true,
  referFriendId,
  nextUrl: _nextUrl,
  customHandler,
  currentForm,
  hideFacebookButton,
  hideFacebookLink,
  friendInvitationId,
  authType = 'signIn',
}) => {
  const { t } = useTranslation();

  const {
    gmailButtonProps,
    facebookButtonProps,
    setupAndInitSocialAuthScripts,
    authState: { processingSocialAuth, loadingFacebookAndGoogleScripts },
  } = useSocialAuth({
    nextUrl: friendInvitationId ? '/premium/' : _nextUrl,
    referFriendId,
    friendInvitationId,
    onSuccess: customHandler,
    hideFacebookLink,
    getNextUrl: friendInvitationId ? undefined : getNextUrl,
  });

  useEffect(() => {
    setupAndInitSocialAuthScripts();
  }, []);

  const facebookLabel =
    authType === 'signUp'
      ? t('LOGIN_WITH_FACEBOOK_SING_UP', 'Regístrate con Facebook')
      : t('LOGIN_WITH_FACEBOOK_SIGN_IN', 'Ingresar con Facebook');
  const googleLabel =
    authType === 'signUp'
      ? t('LOGIN_WITH_GOOGLE_SING_UP', 'Regístrate con Google')
      : t('LOGIN_WITH_GOOGLE_SIGN_IN', 'Ingresar con Google');

  return (
    <SocialButtonsMinimal
      shouldRenderFacebookButton={shouldRenderFacebookButton}
      facebookButtonProps={facebookButtonProps}
      gmailButtonProps={gmailButtonProps}
      processingSocialAuth={processingSocialAuth}
      currentForm={currentForm}
      loadingFacebookAndGoogleScripts={loadingFacebookAndGoogleScripts}
      hideFBAuthButton={hideFacebookButton}
      hideFBAuthLink={hideFacebookLink}
      nextUrl={_nextUrl}
      facebookLabel={facebookLabel}
      googleLabel={googleLabel}
    />
  );
};

export default SocialButtons;
